import constate from "constate";
import { useCallback, useRef, useState } from "react";
import { getUser } from "api/user";
import { compiledClientConfig } from "config";
import type { UserModel } from "models/UserModel";
import type { TLoginDetails } from "utils/gainsight";

const useAuthenticatedUser = () => {
	const [user, setUser] = useState<UserModel | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const [loading, setLoading] = useState(true);
	const loginDetails = useRef<TLoginDetails>();

	const isLoggedIn = user != null;

	const updateUser = useCallback((newUser: UserModel) => {
		const mutatedUsersArray = (compiledClientConfig.mutatedUsers || "").split(",");
		if (mutatedUsersArray.includes(newUser.email)) {
			const mutatedUser = newUser
				.set("familyName", newUser.familyName + " 🏳️‍🌈")
				.set("givenName", "🏳️‍🌈 " + newUser.givenName);
			setUser(mutatedUser);
		} else {
			setUser(newUser);
		}
	}, []);

	const loadUser = useCallback(
		async (newLoginDetails?: TLoginDetails) => {
			setError(null);
			try {
				setLoading(true);
				const newUser = await getUser();
				updateUser(newUser);
				if (newLoginDetails) {
					loginDetails.current = newLoginDetails;
				}
			} catch (error) {
				setError(error as Error);
			} finally {
				setLoading(false);
			}
		},
		[updateUser]
	);

	const clearUser = useCallback(() => {
		setUser(null);
		setError(null);
	}, []);

	return {
		state: { user, isLoggedIn, error, loading, loginDetails: loginDetails.current },
		actions: { loadUser, clearUser, updateUser }
	};
};

export const [AuthenticatedUserProvider, useAuthenticatedUserContext] = constate(useAuthenticatedUser);
